import { ClientConfig, ClientConfigOptions } from '../../clientConfig';
import { Request } from '../../common/request';
import { ApiResult } from '../../common/types';
import { 
  GetUserInfoParams, 
  PersonalAuthenticationV2Params, 
  QueryAuthenticationResultParams, 
  SubmitAuthenticationParams,
  SendSmsParams
} from './model';

/**
 * 个人用户客户端类
 * 提供个人用户相关的API操作，包括用户信息管理、实名认证等功能
 * @class PersonageClient
 */
export class PersonageClient {
   /** 客户端配置 */
   private config: ClientConfig;
   /** HTTP请求服务 */
   private service: Request;

   /**
    * 创建个人用户客户端实例
    * @param {ClientConfigOptions} options - 客户端配置选项
    */
   constructor(options: ClientConfigOptions) {
      this.config = new ClientConfig(options);
      this.service = new Request(this.config.getConfig());
   }

   /**
    * 获取用户信息
    * 获取指定用户的详细信息
    * 
    * @param {GetUserInfoParams} params - 获取用户信息的请求参数
    * @param {string|number} params.userId - 用户ID，必填参数，用于指定要查询的用户
    * @param {string} [params.includeFields] - 可选，指定要返回的字段，多个字段用逗号分隔
    * @param {boolean} [params.includeVerification] - 可选，是否包含用户认证信息
    * @param {boolean} [params.includeSignatures] - 可选，是否包含用户签名信息
    * @param {any} [params.otherParams] - 其他可选参数，根据API需求可扩展
    */
   async getUserInfo(params: GetUserInfoParams): Promise<ApiResult> {
      return this.service.get(`/app/signUsers/getUserInfo`, params);
   }

   /**
    * 获取个人认证页面接口（H5 和 PC）
    * 获取个人用户实名认证的页面URL
    * 
    * @param {PersonalAuthenticationV2Params} params - 请求参数对象
    * @returns {Promise<ApiResult>} 认证页面信息
    */
   async postPersonalAuthenticationV2(params: PersonalAuthenticationV2Params): Promise<ApiResult> {
      return this.service.post('/openAPI/personage/personalAuthenticationV2', params);
   }

   /**
    * 个人实名信息认证结果查询
    * 查询用户的实名认证状态和结果
    * 
    * @param {QueryAuthenticationResultParams} params - 请求参数对象
    * @returns {Promise<ApiResult>} 认证结果信息
    */
   async queryAuthenticationResult(params: QueryAuthenticationResultParams): Promise<ApiResult> {
      return this.service.get('/openAPI/personage/authenticationResult', params);
   }

   /**
    * 个人实名认证接口
    * 提交个人用户的实名认证信息
    * 
    * @param {SubmitAuthenticationParams} params - 请求参数对象
    * @returns {Promise<ApiResult>} 认证提交结果
    */
   async submitAuthentication(params: SubmitAuthenticationParams): Promise<ApiResult> {
      return this.service.post('/openAPI/personage/v1/user/auth', params);
   }

   /**
    * 发送短信验证码
    * 发送短信验证码到指定手机号
    * 
    * @param {SendSmsParams} params - 请求参数对象
    * @returns {Promise<ApiResult>} 短信验证码信息
    */
   async sendSms(params: SendSmsParams): Promise<ApiResult> {
      return this.service.post('/openAPI/v2/api/send_sm', params);
   }
}
